import type {
  EventHallDto,
  EventMapResponse,
  FloorplanScene,
  MapBoothDto,
  UpdateFloorplanCropInput,
  UpdateMapBoothInput,
} from "@mvk/shared";
import { apiRequest } from "./client";

export const mapApi = {
  get(eventId: string, audience: "visitor" | "admin" = "visitor", includeLogos = false) {
    const q = includeLogos ? "?includeLogos=1" : "";
    return apiRequest<EventMapResponse>(`/events/${eventId}/map${q}`, { audience });
  },

  updateBooth(
    eventId: string,
    companyId: string,
    body: UpdateMapBoothInput,
    audience: "visitor" | "admin" = "admin",
  ) {
    return apiRequest<{ booth: MapBoothDto }>(
      `/events/${eventId}/booths/${companyId}`,
      { method: "PATCH", body, audience },
    );
  },

  uploadFloorplan(
    eventId: string,
    hallId: string,
    floorplanSvg: string,
    boardWidth?: number,
    boardHeight?: number,
  ) {
    return apiRequest<{ hall: EventHallDto }>(
      `/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan`,
      {
        method: "PUT",
        body: { floorplanSvg, boardWidth, boardHeight },
        audience: "admin",
      },
    );
  },

  /** Upload .svg or CorelDRAW .cdr (server converts CDR → SVG when LibreOffice is available). */
  uploadFloorplanFile(eventId: string, hallId: string, file: File) {
    const form = new FormData();
    form.append("file", file, file.name);
    return apiRequest<{
      hall: EventHallDto;
      source?: "svg" | "cdr";
      boothCodes?: string[];
      converter?: string;
    }>(`/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan-file`, {
      method: "POST",
      body: form,
      audience: "admin",
    });
  },

  updateFloorplanBoard(eventId: string, hallId: string, boardWidth: number, boardHeight: number) {
    return apiRequest<{ hall: EventHallDto }>(
      `/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan-board`,
      {
        method: "PATCH",
        body: { boardWidth, boardHeight },
        audience: "admin",
      },
    );
  },

  updateFloorplanCrop(eventId: string, hallId: string, crop: UpdateFloorplanCropInput) {
    return apiRequest<{ hall: EventHallDto }>(
      `/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan-crop`,
      {
        method: "PATCH",
        body: crop,
        audience: "admin",
      },
    );
  },

  rotateFloorplan(
    eventId: string,
    hallId: string,
    quarterTurns: 1 | -1 | 2,
    visualOnly = true,
  ) {
    return apiRequest<{ hall: EventHallDto }>(
      `/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan-rotate`,
      {
        method: "POST",
        body: { quarterTurns, visualOnly },
        audience: "admin",
      },
    );
  },

  fitFloorplan(eventId: string, hallId: string) {
    return apiRequest<{ hall: EventHallDto }>(
      `/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan-fit`,
      {
        method: "POST",
        audience: "admin",
      },
    );
  },

  clearFloorplan(eventId: string, hallId: string) {
    return apiRequest<{ hall: EventHallDto }>(
      `/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan`,
      {
        method: "DELETE",
        audience: "admin",
      },
    );
  },

  /** Save scheme overlays (markers / labels) drawn on the underlay. */
  updateFloorplanScene(eventId: string, hallId: string, scene: FloorplanScene) {
    return apiRequest<{ hall: EventHallDto }>(
      `/events/${eventId}/halls/${encodeURIComponent(hallId)}/floorplan-scene`,
      {
        method: "PUT",
        body: scene,
        audience: "admin",
      },
    );
  },
};
